import { change } from 'redux-form';
import { IStore } from 'redux/IStore';
import { ICart, ICartAction, ICartObject } from 'models/cart/cart';
import { IUserShippingAddress } from 'models/user/shipping/usershippingaddress';
import * as c from 'models/constants';
import * as i18n from 'i18next';
import { IShoppingBasket } from 'models/cart/shoppingbasket';
import { IUpdateBasketRequest } from 'models/cart/updatebasketrequest';
import { IOrder, IOrderRequest, IManualOrderRequest } from 'models/orders/order';
import { IProductQtyUpdateRequest } from '../../../models/cart/productQtyUpdateRequest';
import { IShopShippingOption } from '../../../models/shop/shopshippingoption';
import { IKeyValuePair } from '../../../models/common';
import { actions as notifActions } from 'redux-notifications';
import { setShoppingBasketExpiryDate } from 'helpers/common';
import { IAddBookingItemRequest, IFormAddBookingItemRequest } from 'models/bookappointment';
import { sequentialWithBreak } from '../shop/helpers';
import { requestHelper } from 'redux/helpers';
const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'cart/REQUEST_START';
export const REQUEST_FAILURE: string = 'cart/REQUEST_FAILURE';
export const SUBMIT_CART_SUCCESS: string = 'cart/SUBMIT_CART_SUCCESS';
export const CLEAR_FORM_DATA: string = 'cart/CLEAR_FORM_DATA';
export const NEXT_STEP: string = 'cart/NEXT_STEP';
export const PREVIOUS_STEP: string = 'cart/PREVIOUS_STEP';
export const GET_DEFAULT_SHIPPING_ADDRESS_SUCCESS: string = 'cart/GET_DEFAULT_SHIPPING_ADDRESS_SUCCESS';
export const GET_SHOPPING_BASKET_SUCCESS: string = 'cart/GET_SHOPPING_BASKET_SUCCESS';
export const SET_UPDATE_BASKET_REQUEST: string = 'cart/SET_UPDATE_BASKET_REQUEST';
export const RESET_UPDATE_BASKET_REQUEST: string = 'cart/RESET_UPDATE_BASKET_REQUEST';
export const SET_WIZARD_STEP: string = 'cart/SET_WIZARD_STEP';
export const GET_STRIPE_SESSION_SUCCESS: string = 'cart/GET_STRIPE_SESSION_SUCCESS';
export const RESET_STRIPE_SESSION: string = 'cart/RESET_STRIPE_SESSION';
export const SET_SHOW_SHOPPING_BAG_DROPDOWN: string = 'cart/SET_SHOW_SHOPPING_BAG_DROPDOWN';
export const CREATE_ORDER_SUCCESS: string = 'cart/CREATE_ORDER_SUCCESS';

/** Initial State */
const initialState: ICart = {
	cart: {
		useDefaultShippingAddress: false,
		order: null,
	},
	isFetching: false,
	wizardStep: 1,
	paymentSessionId: null,
	defaultShippingAddress: undefined,
	updateBasketRequest: {
		itemsToRemove: [],
		itemsToUpdate: [],
		itemsToSetShippingOption: [],
		itemsToAdd: [],
	},
	showShoppingBagDropdown: false,
	createdOrder: null,
};

/** Reducer */
export function cartReducer(state = initialState, action: ICartAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SUBMIT_CART_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				error: false,
				cart: action.payload.cart,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				error: false,
				cart: {
					useDefaultShippingAddress: false,
					order: null,
				},
				wizardStep: 1,
				defaultShippingAddress: undefined,
				updateBasketRequest: {
					itemsToRemove: [],
					itemsToUpdate: [],
					itemsToSetShippingOption: [],
					itemsToAdd: [],
				},
			});

		case NEXT_STEP:
			return Object.assign({}, state, {
				wizardStep: state.wizardStep + 1,
			});

		case PREVIOUS_STEP:
			return Object.assign({}, state, {
				wizardStep: state.wizardStep - 1,
			});

		case GET_DEFAULT_SHIPPING_ADDRESS_SUCCESS:
			return Object.assign({}, state, {
				defaultShippingAddress: action.payload.defaultShippingAddress,
				isFetching: false,
			});

		case GET_SHOPPING_BASKET_SUCCESS:
			return Object.assign({}, state, {
				shoppingBasket: action.payload.shoppingBasket,
				isFetching: false,
			});

		case GET_STRIPE_SESSION_SUCCESS:
			return Object.assign({}, state, {
				paymentSessionId: action.payload.paymentSessionId,
				isFetching: false,
			});

		case RESET_STRIPE_SESSION:
			return Object.assign({}, state, {
				paymentSessionId: null,
			});

		case SET_UPDATE_BASKET_REQUEST:
			return Object.assign({}, state, {
				updateBasketRequest: action.payload.updateBasketRequest,
			});

		case RESET_UPDATE_BASKET_REQUEST:
			return Object.assign({}, state, {
				updateBasketRequest: {
					itemsToRemove: [],
					itemsToUpdate: [],
					itemsToSetShippingOption: [],
					itemsToAdd: [],
				},
			});

		case SET_WIZARD_STEP:
			return Object.assign({}, state, {
				wizardStep: action.payload.wizardStep,
			});

		case SET_SHOW_SHOPPING_BAG_DROPDOWN:
			return Object.assign({}, state, {
				showShoppingBagDropdown: action.payload.showShoppingBagDropdown,
			});

		case CREATE_ORDER_SUCCESS:
			return Object.assign({}, state, {
				createdOrder: action.payload.createdOrder,
				isFetching: false,
			});

		default:
			return state;
	}
}

export function addBookingToCart(request: IAddBookingItemRequest) {
	if (!Array.isArray(request)) {
		return (dispatch, getState: () => IStore): Promise<any> =>
			dispatch(resetShoppingBasketShippingOptions())
				.catch((e) => {
					console.warn(e);
				})
				.then(() =>
					requestHelper(
						fetch(
							getState().fetchDefault.baseAPI + 'api/ShoppingBasket/AddBookingItem',
							Object.assign({}, getState().fetchDefault.requestInit, {
								method: 'POST',
								body: JSON.stringify(request),
							}),
						),
					),
				);
	} else {
		return (dispatch, getState: () => IStore): Promise<any> =>
			dispatch(resetShoppingBasketShippingOptions())
				.catch((e) => {
					console.warn(e);
				})
				.then(() =>
					sequentialWithBreak(
						request.map(
							(req) => () =>
								requestHelper(
									fetch(
										getState().fetchDefault.baseAPI + 'api/ShoppingBasket/AddBookingItem',
										Object.assign({}, getState().fetchDefault.requestInit, {
											method: 'POST',
											body: JSON.stringify(req),
										}),
									),
								),
						),
					).catch((err) => {
						dispatch(requestFailure(err));
						throw err;
					}),
				);
	}
}

export function createOrder(order: IOrderRequest | IManualOrderRequest, type: 'normal' | 'manual') {
	return (dispatch, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());
		let endpoint = 'order';
		if (type === 'manual') {
			(order as any).includePartiallyUnavailableResources = true;
			endpoint = 'order/createManualOrder';
		}

		return fetch(
			getState().fetchDefault.baseAPI + 'api/' + endpoint,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(order),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IOrder) => {
						return dispatch(createOrderSuccess(res));
					});
				} else {
					let jsonRes;
					return res
						.json()
						.then((res) => {
							jsonRes = res;
							dispatch(requestFailure(res));
							return Promise.reject(res);
						})
						.catch(() => {
							dispatch(requestFailure(jsonRes));
							return Promise.reject(jsonRes);
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				return Promise.reject(err);
			});
	};
}

/** Async Action Creator */
export function getDefaultShippingAddress() {
	return (dispatch, getState: () => IStore) => {
		if (getState().cart.defaultShippingAddress === undefined) {
			dispatch(requestStart());

			return fetch(
				getState().fetchDefault.baseAPI + 'api/User/ShippingAddresses',
				Object.assign({}, getState().fetchDefault.requestInit),
			)
				.then((res) => {
					if (res.ok) {
						return res.json().then((res: IUserShippingAddress[]) => {
							const defaultShippingAddress = res.find((x) => x.isDefault === true);
							if (defaultShippingAddress) {
								dispatch(getDefaultShippingAddressSuccess(defaultShippingAddress));

								dispatch(change(c.FORM_NAME_CART, 'order.shippingName', defaultShippingAddress.name));
								dispatch(change(c.FORM_NAME_CART, 'order.shippingAddressLine', defaultShippingAddress.addressLine1));
								dispatch(change(c.FORM_NAME_CART, 'order.shippingZIPCode', defaultShippingAddress.zipCode));
								dispatch(change(c.FORM_NAME_CART, 'order.shippingCity', defaultShippingAddress.city));
								dispatch(change(c.FORM_NAME_CART, 'order.shippingCountryKey', defaultShippingAddress.countryKey));
								dispatch(change(c.FORM_NAME_CART, 'order.shippingPhoneNumber', defaultShippingAddress.phoneNumber));
								dispatch(change(c.FORM_NAME_CART, 'order.shippingNote', defaultShippingAddress.note));
								dispatch(
									change(c.FORM_NAME_CART, 'order.shippingCallingCodeKey', defaultShippingAddress.callingCodeKey),
								);

								dispatch(change(c.FORM_NAME_CART, 'useDefaultShippingAddress', true));
							} else {
								dispatch(getDefaultShippingAddressSuccess(null));
							}

							dispatch(change(c.FORM_NAME_CART, 'order.email', getState().userPreferences.email));
						});
					} else {
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								res.status = status;
								res.statusText = statusText;
								dispatch(requestFailure(res));
							})
							.catch(() => {
								const message: any = { status, statusText };
								dispatch(requestFailure(message));
							});
					}
				})
				.catch((err) => {
					dispatch(requestFailure(err));
				});
		}
	};
}

/** Async Action Creator */
export function getShoppingBasket(isCart: boolean = false) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/ShoppingBasket',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok && res.status === 200) {
					return res.json().then((res: IShoppingBasket) => {
						dispatch(getShoppingBasketSuccess(res));
						if (isCart && res.items.length > 0 && getState().cart.wizardStep === c.CART_STEP_EMPTY_SHOPPING_BAG) {
							dispatch(nextStep());
						}
					});
				} else if (res.ok && res.status === 204) {
					dispatch(getShoppingBasketSuccess(null));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							if (message.status === 404) {
								dispatch(getShoppingBasketSuccess(null));
							} else {
								dispatch(requestFailure(message));
							}
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function manualBookingGetShoppingBasket() {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/ShoppingBasket',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok && res.status === 200) {
					return res.json().then((res: IShoppingBasket) => {
						dispatch(getShoppingBasketSuccess(res));
						return { message: 'Shopping basket success', status: 'ok', response: res };
					});
				} else if (res.ok && res.status === 204) {
					return dispatch(getShoppingBasketSuccess(null));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							return dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							if (message.status === 404) {
								return dispatch(getShoppingBasketSuccess(null));
							} else {
								return dispatch(requestFailure(message));
							}
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function manualBookingUpdateShoppingBasket(
	shoppingBasketId: string,
	bookingItemsToAdd: IFormAddBookingItemRequest[],
) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (!shoppingBasketId || !bookingItemsToAdd) {
			return;
		}

		const updateBasketRequest = { id: shoppingBasketId, bookingItemsToAdd: bookingItemsToAdd };

		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/ShoppingBasket/UpdateBasket',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(updateBasketRequest),
			}),
		)
			.then(((res) => {
				if (res.ok) {
					return res.json().then((res: IShoppingBasket) => {
						dispatch(getShoppingBasketSuccess(res));
						dispatch(resetUpdateBasketRequest());
						return res;
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
							return { error: true };
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
							return { error: true, message };
						});
				}
			}) as any)
			.catch((err) => {
				dispatch(requestFailure(err));
				return { error: true, message: err };
			});
	};
}

/** Async Action Creator gets a shopping basket filled with available shipping options for items */
export function setShippingOptions() {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		return fetch(
			getState().fetchDefault.baseAPI + 'api/ShoppingBasket/SetShippingOptions',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok && res.status === 200) {
					return res.json().then((res: IShoppingBasket) => {
						dispatch(getShoppingBasketSuccess(res));
					});
				} else if (res.ok && res.status === 204) {
					dispatch(getShoppingBasketSuccess(null));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							if (message.status === 404) {
								dispatch(getShoppingBasketSuccess(null));
							} else {
								dispatch(requestFailure(message));
							}
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function updateShoppingBasket(cb?: () => void) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		const updateBasketRequest = getState().cart.updateBasketRequest;

		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/ShoppingBasket/UpdateBasket',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(updateBasketRequest),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IShoppingBasket) => {
						dispatch(getShoppingBasketSuccess(res));
						dispatch(resetUpdateBasketRequest());
						if (res.items.length === 0) {
							dispatch(setWizardStep(c.CART_STEP_EMPTY_SHOPPING_BAG));
						}
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			})
			.then(cb);
	};
}

export function clearShoppingBasket(wishlist: boolean) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		let apiUrl = 'api/ShoppingBasket/Clear';
		if (wishlist) {
			apiUrl += '?wishlist=true';
		}

		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + apiUrl,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IShoppingBasket) => {
						dispatch(getShoppingBasketSuccess(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function updateShoppingBasketItemQty(productQtyUpdateRequest: IProductQtyUpdateRequest) {
	return (dispatch, getState: () => IStore) => {
		const updateBasketRequest: IUpdateBasketRequest = getState().cart.updateBasketRequest;
		updateBasketRequest.itemsToUpdate.push(productQtyUpdateRequest);

		const shoppingBasket: IShoppingBasket = getState().cart.shoppingBasket;
		const itemToUpdateIndex = shoppingBasket.items.indexOf(
			shoppingBasket.items.find((x) => x.productId === productQtyUpdateRequest.productId),
		);
		shoppingBasket.items[itemToUpdateIndex].qty = productQtyUpdateRequest.qty;

		dispatch(setUpdateBasketRequest(updateBasketRequest));
		dispatch(getShoppingBasketSuccess(shoppingBasket));
		dispatch(updateShoppingBasket());
	};
}

/** Action Creator */
export function removeShoppingBasketItem(itemId: string) {
	return (dispatch, getState: () => IStore) => {
		const updateBasketRequest: IUpdateBasketRequest = getState().cart.updateBasketRequest;
		updateBasketRequest.itemsToRemove.push(itemId);

		const shoppingBasket: IShoppingBasket = getState().cart.shoppingBasket;
		const itemToRemoveIndex = shoppingBasket.items.indexOf(shoppingBasket.items.find((x) => x.productId === itemId));
		shoppingBasket.items.splice(itemToRemoveIndex, 1);

		dispatch(setUpdateBasketRequest(updateBasketRequest));
		dispatch(getShoppingBasketSuccess(shoppingBasket));
		dispatch(updateShoppingBasket());
	};
}

/** Action Creator - create */
export function setShippingOption(shippingOptionId: string, items: string[]) {
	return (dispatch, getState: () => IStore) => {
		const shoppingBasket = getState().cart.shoppingBasket;
		const updateBasketRequest: IUpdateBasketRequest = getState().cart.updateBasketRequest;

		items.forEach((itemId: string) => {
			updateBasketRequest.itemsToSetShippingOption.push({ key: itemId, value: shippingOptionId });
			const shippingOptions: IShopShippingOption[] = getState().cart.shoppingBasket.items.find(
				(x) => x.productId === itemId,
			).shippingOptions;
			const shippingOption: IShopShippingOption = shippingOptions.find((x) => x.id === shippingOptionId);
			const itemToSetShippingOptionIndex = shoppingBasket.items.findIndex((x) => x.productId === itemId);
			shoppingBasket.items[itemToSetShippingOptionIndex].shippingOptionId = shippingOptionId;
			shoppingBasket.items[itemToSetShippingOptionIndex].shippingOptionName = shippingOption.name;
			shoppingBasket.items[itemToSetShippingOptionIndex].shippingOptionPrice = shippingOption.price;
		});

		dispatch(setUpdateBasketRequest(updateBasketRequest));
		dispatch(getShoppingBasketSuccess(shoppingBasket));
		dispatch(
			updateShoppingBasket(() => {
				dispatch(setShippingOptions());
			}),
		);
	};
}

/** Action Creator */
export function resetShoppingBasketShippingOptions() {
	return (dispatch, getState: () => IStore): Promise<any> => {
		const shoppingBasket: IShoppingBasket = getState().cart.shoppingBasket;
		if (!shoppingBasket?.items?.length) {
			return Promise.resolve(true);
		}
		const itemsToSetShippingOption: IKeyValuePair[] = shoppingBasket.items.map((item) => {
			return { key: item.productId, value: null };
		});

		const updateBasketRequest: IUpdateBasketRequest = {
			itemsToAdd: [],
			itemsToUpdate: [],
			itemsToRemove: [],
			itemsToSetShippingOption,
		};

		dispatch(setUpdateBasketRequest(updateBasketRequest));
		return dispatch(updateShoppingBasket());
	};
}

/** Async Action Creator */
export function getStripeSession(orderId: string, successUrl: string, cancelUrl: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		let queryString = '?';
		queryString = queryString + 'orderId=' + orderId;
		queryString = queryString + '&successUrl=' + successUrl;
		queryString = queryString + '&cancelUrl=' + cancelUrl;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/order/createpayment' + queryString,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: any) => {
						dispatch(getStripeSessionSuccess(res.paymentSessionId));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): ICartAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): ICartAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function clearFormData(): ICartAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

/** Action Creator */
export function createShopSuccess(cart: ICartObject): ICartAction {
	return {
		type: SUBMIT_CART_SUCCESS,
		payload: {
			cart,
		},
	};
}

/** Action Creator */
export function nextStep(): ICartAction {
	return {
		type: NEXT_STEP,
	};
}

/** Action Creator */
export function previousStep(): ICartAction {
	return {
		type: PREVIOUS_STEP,
	};
}

/** Action Creator */
export function setWizardStep(wizardStep: number): ICartAction {
	return {
		type: SET_WIZARD_STEP,
		payload: {
			wizardStep,
		},
	};
}

/** Action Creator */
export function getDefaultShippingAddressSuccess(defaultShippingAddress: IUserShippingAddress): ICartAction {
	return {
		type: GET_DEFAULT_SHIPPING_ADDRESS_SUCCESS,
		payload: {
			defaultShippingAddress,
		},
	};
}

/** Action Creator */
export function getShoppingBasketSuccess(shoppingBasket: IShoppingBasket): ICartAction {
	return {
		type: GET_SHOPPING_BASKET_SUCCESS,
		payload: {
			shoppingBasket,
		},
	};
}

/** Action Creator */
export function getStripeSessionSuccess(paymentSessionId: string): ICartAction {
	return {
		type: GET_STRIPE_SESSION_SUCCESS,
		payload: {
			paymentSessionId,
		},
	};
}

/** Action Creator */
export function resetStripeSession(): ICartAction {
	return {
		type: RESET_STRIPE_SESSION,
	};
}

/** Action Creator */
export function setUpdateBasketRequest(updateBasketRequest: IUpdateBasketRequest): ICartAction {
	return {
		type: SET_UPDATE_BASKET_REQUEST,
		payload: {
			updateBasketRequest,
		},
	};
}

/** Action Creator */
export function resetUpdateBasketRequest(): ICartAction {
	return {
		type: RESET_UPDATE_BASKET_REQUEST,
	};
}

/** Action Creator */
export function setShowShoppingBagDropdown(showShoppingBagDropdown: boolean): ICartAction {
	return {
		type: SET_SHOW_SHOPPING_BAG_DROPDOWN,
		payload: {
			showShoppingBagDropdown,
		},
	};
}

export function handleShoppingBasketSuccess(shoppingBasket: IShoppingBasket) {
	return (dispatch) => {
		dispatch(getShoppingBasketSuccess(shoppingBasket));
		dispatch(setShowShoppingBagDropdown(true));
		dispatch(
			notifSend({
				message: i18n.t('Notification_Success_ProductAddedToShoppingBag'),
				kind: 'success',
				dismissAfter: 3000,
			}),
		);
		setShoppingBasketExpiryDate();
	};
}

/** Action Creator */
export function createOrderSuccess(createdOrder: IOrder): ICartAction {
	return {
		type: CREATE_ORDER_SUCCESS,
		payload: {
			createdOrder,
		},
	};
}
